import "./styles.scss";

import { useState, useEffect } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useApp } from "context/app";
import { useWebsite } from "context/client/website";
import { useWebsiteAccessData } from "context/client/website_access_data";

import usePaymentVerification from "./hooks/usePaymentVerification";

import DashboardContentLayout from "components/layouts/DashboardContentLayout";
import DetailsCard from "components/DetailsCard";
import DetailsCardList from "components/DetailsCardList";
import DevelopmentMobile from "components/DevelopmentMobile";
import {
  BillingDataModal,
  ContactDataModal,
  CancelPlanModal,
} from "./components";
import { AccessDataModal } from "page_components/shared/tasks/modals";
import ScrollTopMobile from "components/ScrollTopMobile";

import countries from "libs/countries.json";
import task_status_data from "libs/task_status_data.json";

import useResponsive from "libs/helpers/useResponsive";
import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";
import getCountryFromCode from "libs/helpers/getCountryFromCode";
import priceRender from "libs/helpers/priceRender";
import plans_data from "libs/plans_data";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as ThundersIcon } from "assets/icons/new/thunders.svg";
import { ReactComponent as SummaryIcon } from "assets/icons/new/summary.svg";
import { ReactComponent as ErrorIcon } from "assets/icons/new/error.svg";

const mainClass = "websites-client-layout";

const WebsitesContent = () => {
  const { t } = useTranslation();
  const { max } = useResponsive();
  const navigate = useNavigate();

  const { plans_list, websites } = useApp();
  const { data, loading, error } = useWebsite();

  const { verify, loading: paymentLoading } = usePaymentVerification();
  const { data: accessData, updateData: updateAccessData } =
    useWebsiteAccessData();

  const [contact_data_modal, setContactDataModal] = useState(false);
  const [billing_data_modal, setBillingDataModal] = useState(false);
  const [cancel_plan_modal, setCancelPlanModal] = useState(false);
  const [access_data_modal, setAccessDataModal] = useState(false);

  const toNumber = (value) => {
    const serializedValue =
      value?.$numberDecimal ??
      value?.$numberDouble ??
      value?.$numberInt ??
      value?.$numberLong ??
      value?.value ??
      value;

    if (serializedValue == null || serializedValue === "") return null;

    const numericValue = Number(serializedValue);

    return Number.isFinite(numericValue) ? numericValue : null;
  };

  const formatTime = (value, zeroAsUnavailable = false) => {
    const numericValue = toNumber(value);

    return numericValue === null || (zeroAsUnavailable && numericValue === 0)
      ? "-"
      : toHoursAndMinutes(numericValue)?.formatted;
  };

  let subscription_data = null;
  plans_list.forEach((item, index) => {
    item?.prices?.forEach((price) => {
      if (price.id === data?.subscription?.price_id) {
        subscription_data = { ...plans_data?.[index], ...item, ...price };
      }
    });
  });

  const status_notification_label = (status) => {
    switch (status) {
      case 1:
        return t("Everything is fine");
      case 2:
        return t("Something is wrong");
      case 3:
        return t("Awaiting verification");
      case 4:
        return t("Website does not respond");
      default:
        return "-";
    }
  };

  const status_notification_description = (status) => {
    switch (status) {
      case 1:
        return t(
          "Your website is safe and up to date. Your payment method is active.",
        );
      case 2:
        return t(
          "We have detected a problem with your website. We will verify it and fix it, or get back to you with further information",
        );
      case 3:
        return t(
          "It may take up to 24h for us to verify if everything is ok with your website",
        );
      case 4:
        return t(
          "There may be a problem with your website or its server. We have received an automatic notification of the issue and are working to resolve it.",
        );
      default:
        return "-";
    }
  };

  const actions = [
    {
      label: t("Add new website"),
      href: "/websites/new",
      event: () =>
        window.gtag("event", "new_website", {
          click_text: t("Add new website"),
          click_url: `/websites/new`,
        }),
    },
    ...(websites?.length > 0
      ? [
          {
            type: "create_client_task",
            onboarding_button: true,
          },
        ]
      : []),
  ];

  const overview_data = {
    title: t("Overview"),
    iconMobile:
      data?.status === 1 ? (
        <ThundersIcon />
      ) : data?.status === 3 ? (
        <SummaryIcon />
      ) : (
        <ErrorIcon />
      ),
    notifications: [
      {
        status: data?.status,
        label: status_notification_label(data?.status),
        description: status_notification_description(data?.status),
      },
    ],
    items: [
      {
        label: t("Website URL"),
        value: data?.website,
      },
      {
        label: t("Last CMS update"),
        value: data?.last_cms_update
          ? moment(data?.last_cms_update).format("ll")
          : "-",
      },
      {
        label: t("SSL certificate valid until"),
        value: data?.ssl_valid_until
          ? moment(data?.ssl_valid_until).format("ll")
          : "-",
      },
      {
        label: t("Domain valid until"),
        value: data?.domain_valid_until
          ? moment(data?.domain_valid_until).format("ll")
          : "-",
      },
      {
        label: t("Time available in your plan"),
        value: toHoursAndMinutes(data?.time_used?.available_in_plan)?.formatted,
        hiddenMobile: true,
      },
      {
        label: t("Time used"),
        value: toHoursAndMinutes(data?.time_used?.time_used)?.formatted,
        hiddenMobile: true,
      },
      {
        label: t("Time left"),
        value: toHoursAndMinutes(data?.time_used?.time_left)?.formatted,
        hiddenMobile: true,
      },
      {
        label: t("Time exceeding plan"),
        value: toHoursAndMinutes(data?.time_used?.time_exceeding_plan)
          ?.formatted,
        hiddenMobile: true,
      },
    ],
  };

  const details_data = [
    {
      title: t("Contact data"),
      action: {
        label: t("Edit"),
        iconMobile: <ArrowIcon />,
        onclick: () => setContactDataModal(true),
      },
      items: [
        {
          label: t("Full name"),
          value: `${data?.contact_data?.first_name} ${data?.contact_data?.last_name}`,
        },
        {
          label: t("Email"),
          value: data?.contact_data?.email,
        },
        {
          label: t("Phone"),
          value: `${
            countries?.find(
              (item) => item?.code === data?.contact_data?.phone?.code,
            )?.dialling_code || ""
          } ${data?.contact_data?.phone?.number}`,
        },
      ],
    },
    {
      title: t("Tasks"),
      action: {
        label: t("Show"),
        iconMobile: <ArrowIcon />,
        href: `/tasks?website=${data?._id}`,
      },
      items: task_status_data
        .filter((item) => item?.key !== "canceled")
        .map((status) => ({
          label: t(status?.label),
          value:
            data?.tasks?.filter((task) => task?.status === status?.value)
              ?.length || "-",
          status: status?.value,
        })),
    },
    {
      title: t("Website access data"),
      action: {
        label: t("View / Edit"),
        iconMobile: <ArrowIcon />,
        onclick: () => setAccessDataModal(true),
      },
      items: [
        {
          value: t(
            "Here you can update access data to your website. The data includes access to FTP server, database and CMS WordPress.",
          ),
        },
      ],
    },
  ];

  const payment_data = [
    {
      variantMobile: "plan",
      title: t("Plan"),
      subscription_end:
        !!!data?.subscription?.next_payment &&
        !!data?.subscription?.start_payment
          ? moment?.unix(data?.subscription?.start_payment)?.add(1, "months")
          : null,
      action: [
        ...(!!data?.subscription?.next_payment
          ? [
              {
                label: t("Change plan"),
                iconMobile: <ArrowIcon />,
                href: "change-plan",
              },
              {
                label: t("Cancel plan"),
                iconMobile: <ArrowIcon />,
                onclick: () => setCancelPlanModal(true),
              },
            ]
          : [
              {
                label: t("Change plan"),
                iconMobile: <ArrowIcon />,
                href: "change-plan",
              },
            ]),
      ],
      items: [
        {
          label: t("Type"),
          value: `${subscription_data?.name} plan`,
          hiddenMobile: true,
        },
        {
          custom_children: (
            <div className={`${mainClass}__plan-data`}>
              <i>{subscription_data?.icon}</i>
              <span>{subscription_data?.name}</span>
            </div>
          ),
          hiddenDesktop: true,
        },
        {
          label: t("Billing cycle"),
          value:
            subscription_data?.contract_period === "monthly"
              ? t("Monthly")
              : t("Yearly"),
        },
        {
          label: t("Amount"),
          value: priceRender(
            data?.subscription?.amount / 100,
            data?.subscription?.currency,
          ),
        },
        {
          label: t("Next charge"),
          value: !!data?.subscription?.next_payment
            ? moment.unix(data?.subscription?.next_payment)?.format("ll")
            : "-",
        },
      ],
    },
    {
      title: t("Payment"),
      view: data?.subscription?.status !== "active" ? "payment_error" : null,
      action: {
        label:
          data?.subscription?.status === "active" || max.sm
            ? t("Edit")
            : t("Verify the payment method"),
        iconMobile:
          data?.subscription?.status === "active" || max.sm ? (
            <ArrowIcon />
          ) : null,
        loading: paymentLoading,
        onclick: verify,
      },
      items: [
        {
          type: "card",
          label: t("Method"),
          value: {
            card_type: data?.subscription?.card?.brand,
            status: !!data?.subscription?.card?.expires_soon
              ? 2
              : data?.subscription?.status === "active"
                ? 1
                : 0,
            card_numbers: !!data?.subscription?.card?.last4
              ? `**** **** **** **** ${data?.subscription?.card?.last4}`
              : data?.subscription?.status === "active"
                ? "Link"
                : "-",
          },
        },
        {
          type: "button",
          label: t("Invoices"),
          value: {
            button_label: t("Show"),
            button_href: `/invoices?website=${data?._id}`,
          },
        },
      ],
    },
    {
      title: t("Invoice details"),
      action: {
        label: t("Edit"),
        iconMobile: <ArrowIcon />,
        onclick: () => setBillingDataModal(true),
      },
      items: [
        {
          label: t("Name"),
          value: data?.billing_data?.name,
        },
        {
          label: t("Tax identification number"),
          value: `${data?.billing_data?.tax?.code} ${data?.billing_data?.tax?.clean_number}`,
        },
        {
          label: t("Address"),
          value: (
            <>
              {data?.billing_data?.street}
              <br />
              {data?.billing_data?.post_code} {data?.billing_data?.city}
              <br />
              {t(getCountryFromCode(data?.billing_data?.country)?.name)}
            </>
          ),
          align: "top",
        },
      ],
    },
  ];

  useEffect(() => {
    if (error) navigate("/");
  }, [error, navigate]);

  if (error) return null;

  return (
    <>
      <DashboardContentLayout
        header={{
          label: data?.website,
          description: t("Detailed information about your payment."),
        }}
        actions={actions}
        loading={loading}
      >
        <div className={mainClass}>
          <div className={`${mainClass}__col`}>
            <DetailsCard
              {...overview_data}
              {...(max.sm
                ? {
                    footer: (
                      <DevelopmentMobile
                        available={formatTime(
                          data?.time_used?.available_in_plan,
                          true,
                        )}
                        left={formatTime(data?.time_used?.time_left, true)}
                        exceeding={formatTime(
                          data?.time_used?.time_exceeding_plan,
                        )}
                      />
                    ),
                  }
                : {})}
            >
              <DetailsCardList
                items={overview_data?.items}
                view={overview_data?.view}
              />
            </DetailsCard>
          </div>
          <div className={`${mainClass}__col`}>
            {details_data?.map((data, index) => (
              <DetailsCard key={index} {...data}>
                <DetailsCardList
                  {...(data?.title === t("Tasks")
                    ? { variant: "tasks", websiteId: data?._id }
                    : {})}
                  items={data?.items}
                  view={data?.view}
                />
              </DetailsCard>
            ))}
          </div>
          <div className={`${mainClass}__col ${mainClass}__col--payment`}>
            {payment_data?.map((data, index) => (
              <DetailsCard key={index} {...data}>
                <DetailsCardList items={data?.items} view={data?.view} />
              </DetailsCard>
            ))}
          </div>
        </div>
        {max.sm && (
          <div className={`${mainClass}__scroll`}>
            <ScrollTopMobile />
          </div>
        )}
      </DashboardContentLayout>

      <ContactDataModal
        isOpen={contact_data_modal}
        onClose={() => setContactDataModal(false)}
      />
      <BillingDataModal
        isOpen={billing_data_modal}
        onClose={() => setBillingDataModal(false)}
      />
      <CancelPlanModal
        isOpen={cancel_plan_modal}
        subscription_end={data?.subscription?.next_payment}
        onClose={() => setCancelPlanModal(false)}
      />
      <AccessDataModal
        data={accessData}
        updateData={updateAccessData}
        isOpen={access_data_modal}
        onClose={() => setAccessDataModal(false)}
      />
    </>
  );
};

export default WebsitesContent;
